/**
 * retireScheduledTask.ts — the ONE way a scheduled task is switched off.
 * #1966, her item 11's last line: "pull retire-and-null-the-slot into one
 * shared helper".
 *
 * ── WHAT THE PATTERN IS, AND WHY IT HAD THREE COPIES ───────────────────────
 *
 * Retiring a `scheduled_tasks` row is TWO writes that have to happen together:
 *
 *     is_active = false      — stop it running
 *     next_run_at = null     — and take away the slot it would run AT
 *
 * ⚠️ The second one is the one that gets forgotten, and forgetting it is not
 * untidiness. The runner's due query is `is_active = true AND next_run_at <=
 * now()` with no staleness guard, so a retired row still holding a past slot
 * fires THE MOMENT anything flips the flag back on — a resume, an Undo, a
 * toggle, a trigger. She crosses something off and a text about it arrives
 * when she switches something unrelated back on.
 *
 * That exact defect was found and fixed three separate times, on three task
 * types, by three issues: #1920 (one-offs), #1924 PR 3 (recurring reminders)
 * and #1957 (`reminder_nudge`, which had been writing `is_active: false` and
 * nothing else). Each fix was correct and each was local, so the fourth type
 * to need it — "💬 Text me about this one" — would have been the fourth
 * rediscovery. Hence one helper, and the two existing patch builders now
 * delegate to it rather than restating it.
 *
 * ── ⚠️ WHY THE CONFIG IS SPREAD AND NOT REBUILT ────────────────────────────
 *
 * A task's `config` carries things SHE chose — `help_types`,
 * `task_description`, the recurrence, the event title. Every caller here has a
 * typed parser that returns only the fields it validates, so writing that
 * parsed object back would silently drop the rest. The base object is spread
 * and the stamp is merged on top.
 *
 * ── WHAT THIS DELIBERATELY DOES NOT DO ─────────────────────────────────────
 *
 * It does not write. `writeScheduledTaskVerified` in TasksTab owns that, and it
 * reads the write back (house rule 16: PostgREST answers 200 + `[]` for a
 * zero-row update, so an unread update is not evidence of anything). This
 * builds the patch; the caller is responsible for proving it landed.
 *
 * It also does not decide WHETHER to retire. "Done", "ended early" and "the
 * event moved out of reach" are different occasions with different stamps and
 * different copy, which is why the stamp is a parameter.
 */

/** The shape every retire patch has. Callers spread it into one update.
 *
 *  ⚠️ A `type` and not an `interface`, and that is load-bearing. TypeScript
 *  gives an anonymous object type an IMPLICIT INDEX SIGNATURE and an interface
 *  none, so declaring this as an interface makes it un-assignable to the
 *  `Record<string, unknown>` parameter that `writeScheduledTaskVerified` and
 *  the optimistic-state spreads take — which is how the inline types this
 *  replaced were being consumed. Switching to `interface` compiles here and
 *  breaks every call site. */
export type RetireScheduledTaskPatch = {
  is_active: false;
  next_run_at: null;
  config: Record<string, unknown>;
};

/**
 * Builds the patch that switches a task off and clears its slot.
 *
 * @param rawConfig the row's CURRENT config, spread into the result untouched.
 * @param stamp     the reason, merged on top — `{ done_at }`, `{ ended_early:
 *                  true }`, `{ retired_reason }`. Keys set to `undefined` are
 *                  dropped, so a caller can clear a previous stamp in the same
 *                  patch without a second object.
 */
export function retireScheduledTaskPatch(
  rawConfig: unknown,
  stamp: Record<string, unknown> = {},
): RetireScheduledTaskPatch {
  const base =
    rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig)
      ? (rawConfig as Record<string, unknown>)
      : {};
  const config: Record<string, unknown> = { ...base, ...stamp };
  // ⚠️ `undefined` is not a jsonb value — PostgREST drops the key on the way
  // out, which is the behaviour the `delete` makes explicit here rather than
  // leaving to chance. It is how a caller RETRACTS a stamp (Undo dropping
  // `done_at`) through the same helper.
  for (const key of Object.keys(stamp)) {
    if (stamp[key] === undefined) delete config[key];
  }
  return { is_active: false, next_run_at: null, config };
}
